/** @ngInject */
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { first, take } from 'rxjs/operators';
import { BxLogger } from '../../../core/services/bx-logger.service';
import { DialogParams, DialogService } from '../../../core/services/dialog.service';
import { IntercomService } from '../../../core/services/intercom.service';
import { LaunchDarklyService } from '../../../core/services/launchdarkly.service';
import { UserMfaActionType, UserService } from '../../../core/services/user';
import { RepoAccountService } from '../../../data/repositories/repo-account.service';
import { RepoAuthService } from '../../../data/repositories/repo-auth.service';
import { RepoSettingsService } from '../../../data/repositories/repo-settings.service';
import { SharedDataService } from '../../../data/services/shared-data.service';
import { QuickstartServiceService } from '../../dashboard/dashboard-quickstart/quickstart-service.service';
import { LayoutService } from '../layout.service';
import { AccountUser } from './../../../data/models/index';

@Component({
    selector: 'app-form-my-profile',
    templateUrl: './sidebar2-form-myprofile.component.html',
    standalone: false,
})
export class Sidebar2FormMyProfileComponent implements OnInit {
    @Output() showAppVersionTrigger = new EventEmitter<boolean>();

    // @ts-ignore TS7008
    user;
    // @ts-ignore TS2564
    showIntercomLauncher: boolean;
    // @ts-ignore TS2564
    mfaEnabled: boolean;
    // @ts-ignore TS2564
    mfaForced: boolean;
    // @ts-ignore TS2564
    preToggleMfaCheck: Observable<boolean>;
    // @ts-ignore TS2564
    showQuickStart: boolean;
    isSmallViewport: boolean = false;
    showRemovePassword = false;
    #timesShowOnboardingToggled = 0;

    constructor(
        private readonly authService: RepoAuthService,
        private repoAccount: RepoAccountService,
        private settings: RepoSettingsService,
        private intercomService: IntercomService,
        private userService: UserService,
        private sharedData: SharedDataService,
        private dialogService: DialogService,
        private readonly quickstartService: QuickstartServiceService,
        private logger: BxLogger,
        private layout: LayoutService,
        private lds: LaunchDarklyService
    ) {}

    ngOnInit() {
        this.user = this.sharedData.UserData.user;
        this.showIntercomLauncher = this.settings.ShowIntercomLauncher;
        this.showQuickStart = this.settings.ShowQuickStart;
        this.isSmallViewport = this.layout.isSmallViewport();
        this.showRemovePassword = this.lds.get('remove-mfa-setup-cancel-button');

        this.userService.mfaStatus$.subscribe((status) => {
            this.mfaEnabled = status.enabled;
            this.mfaForced = status.forced;
        });

        this.preToggleMfaCheck = new Observable((observer) => {
            if (this.mfaEnabled) {
                observer.next(false);
                observer.complete();
                const params: DialogParams = {
                    title: 'Security measure before disabling 2-Step Verification',
                    text:
                        'In order to proceed and switch off 2-step verification we need to verify your identity first. Make sure you have access to your ' +
                        '<a class="swal-link" href="https://help.buildxact.com/en/articles/4702517-2-step-verification-recommended-authenticator-apps" target="_blank" >authenticator app</a> ' +
                        'of choice.',
                    okText: 'Verify',
                    cancelText: 'Cancel, do it another time',
                    allowHtml: true,
                };
                this.dialogService.show(params).then(() => {
                    this.authService.auth0LoginWithDisableMfaCheck();
                });
            } else {
                const params: DialogParams = {
                    type: 'success',
                    title: 'Enabling 2-Step Verification',
                    text:
                        "You're about to set up 2-Step Verification. <br> <br> Once enabled, the next time you login you'll be asked to authenticate your account through an " +
                        '<a class="swal-link" href="https://help.buildxact.com/en/articles/4702517-2-step-verification-recommended-authenticator-apps" target="_blank" >authenticator app.</a>',
                    okText: 'Log out and set up now',
                    cancelText: 'Cancel',
                    allowHtml: true,
                };

                this.dialogService.show(params).then(
                    () => {
                        observer.next(true);
                        observer.complete();
                    },
                    () => {
                        observer.next(false);
                        observer.complete();
                    }
                );
            }
        });
    }

    toggleIntercomLauncher() {
        this.showIntercomLauncher = !this.showIntercomLauncher;
        this.settings.ShowIntercomLauncher = this.showIntercomLauncher;
        this.intercomService.updateLauncherSettings();
    }

    // @ts-ignore TS7006
    toggleMfa(e) {
        const toggle = !!e;
        this.userService
            .toggleMfa(UserMfaActionType.MfaToggle, toggle)
            .pipe(take(1))
            .subscribe(() => {
                if (toggle) {
                    this.repoAccount.logOut();
                }
            });
    }

    confirmResetPassword() {
        const params: DialogParams = {
            type: 'info',
            title: this.sharedData.UserData.user.UserHasSetupPassword === true ? 'Reset Password' : 'Set Password',
            text: `Please confirm you wish to ${
                this.sharedData.UserData.user.UserHasSetupPassword === true ? 'reset the password' : 'set password'
            } for this user. A link will be sent via email.`,
            okText: 'OK',
            cancelText: 'Cancel',
        };

        this.dialogService.show(params).then(
            () => {
                this.repoAccount.SendChangePasswordEmail(this.user.UserName);
            },
            () => {}
        );
    }

    confirmRemovePassword() {
        const params: DialogParams = {
            type: 'info',
            title: 'Remove your password',
            text: 'If you remove your password, you will only be able to login via sending a code to your email address.',
            okText: 'OK',
            cancelText: 'Cancel',
        };

        this.dialogService.show(params).then(
            () => {
                this.authService
                    .removePassword()
                    .pipe(first())
                    .subscribe(() => {
                        this.sharedData.UserData.user.UserHasSetupPassword = false;
                        this.logger.success('Your password has been removed');
                    });
            },
            () => {}
        );
    }

    toggleQuickStart() {
        this.showQuickStart = !this.showQuickStart;
        this.settings.ShowQuickStart = this.showQuickStart;
        this.quickstartService.enabledStateChanged();

        this.#timesShowOnboardingToggled += 1;
        if (this.#timesShowOnboardingToggled >= 6) {
            this.showAppVersionTrigger.emit(true);
        }
    }

    toggleSocialAccountLogin() {
        const userToUpdate: AccountUser = { ...this.user };
        userToUpdate.userId = this.user.ID_User;
        userToUpdate.lastName = this.user.Surname;
        userToUpdate.enableSocialAccountLogin = !this.user.EnableSocialAccountLogin;
        this.repoAccount.UpdateUser(userToUpdate).then(
            (result) => {
                if (result.status === 200) {
                    this.user.EnableSocialAccountLogin = userToUpdate.enableSocialAccountLogin;
                    this.logger.success(`Social Account Login is ${this.user.EnableSocialAccountLogin === true ? 'enabled' : 'disabled'}`);
                } else {
                    this.logger.error('Unable to update Social Account Login');
                }
            },
            (error) => {
                this.logger.error(error);
            }
        );
    }
}
